import { useEffect } from "react";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import {
  AppLaunch,
  Door,
  DoorActions,
  DoorButton,
  DoorChoice,
  DoorChoices,
  DoorFine,
  DoorText,
  LaunchScreen,
  OrganisationMark,
} from "@optymyse/suite-ui";
import { onlyWorkspace, organisationIcon, useWorkspace } from "../lib/workspaces";
import { useApps } from "../lib/apps";
import { useMe, useSignOut } from "../lib/auth";
import { ApiError } from "../lib/api";
import { centralUrl, currentWorkspaceSlug, onCentralHost, signInUrl } from "../lib/hosts";
import { WorkspaceAccessNotice } from "../components/workspaces/WorkspaceAccessNotice";
import { ToWorkspaces } from "../components/workspaces/ToWorkspaces";
import { LinkFrame } from "../components/layout/LinkFrame";
import { strings } from "../lib/strings";
import { PRODUCT_KEY } from "../../shared/product";

// `/admin` is the console (Impa docs/PLATFORM_CONVENTIONS.md, rule 8). On a workspace's own host it is
// that workspace's: a layout whose pages are nested children (the customers, the contacts, the settings).
// On the central host it is the person's list of workspaces, as Sustain's and Steer's are, and none of the
// workspace pages are shown there.
function Console() {
  if (onCentralHost()) return <WorkspaceChooser />;
  if (currentWorkspaceSlug() === null) return <ToWorkspaces />;
  return <WorkspaceLayout />;
}

// Every page under a workspace reads the same workspace query, and Link answers it only once Optymyse's
// access decision allows the person in. Somebody with no session cookie never gets here: the Worker
// sends them to sign in first (src/worker/console.ts). One whose session has ended since is sent the
// same way from here, back to this page; a refusal (403) or an unavailable decision (503) is shown here,
// for the whole workspace, instead of each page sending the person away. Until the decision is in,
// Link's launch shows (SuiteUi's AppLaunch, carrying on from the one in the page the Worker served),
// then fades over the suite's frame (LinkFrame): the top bar with the person's apps and account, and
// the navigation.
function WorkspaceLayout() {
  const slug = currentWorkspaceSlug()!;
  const view = useWorkspace(slug);
  // The frame's own list, asked for beside the decision rather than after it.
  useApps();
  if (view.error instanceof ApiError && view.error.status === 401) return <SignIn returnTo={window.location.href} />;
  if (view.error instanceof ApiError && (view.error.status === 403 || view.error.status === 503)) {
    return <WorkspaceAccessNotice status={view.error.status} />;
  }
  return (
    <AppLaunch product={PRODUCT_KEY} ready={!view.isPending}>
      <LinkFrame>
        <Outlet />
      </LinkFrame>
    </AppLaunch>
  );
}

/**
 * The person's workspaces, as Sustain's and Steer's `/admin` serve them: nobody signed in is sent to sign in;
 * one workspace, with none Optymyse couldn't check, opens directly, with Link's launch while it is decided; several,
 * or some unchecked, are the suite's door's choice of workspaces, as Assure's and Unite's are, each by its
 * organisation's name and icon logo as Impa holds them, never its address, with "Not you? Sign out"; none is the door,
 * "No Link workspace yet"; and when Optymyse can't be asked about any of them, the door says so.
 */
function WorkspaceChooser() {
  const me = useMe();
  const signOut = useSignOut();
  const answer = me.data;
  const only = onlyWorkspace(answer);
  useEffect(() => {
    if (only) window.location.replace(only.console_url);
  }, [only]);

  if (me.isLoading || only) return <LaunchScreen product={PRODUCT_KEY} />;
  if (answer?.kind === "signed_out") return <SignIn returnTo={centralUrl("/admin")} />;
  if (answer?.kind === "no_workspace" || (answer?.kind === "signed_in" && answer.me.workspaces.length === 0)) {
    // A workspace is an organisation's at Optymyse, and Link learns of a new one when the person signs
    // in: signing in again is how an invitation accepted since shows up here.
    return (
      <Door product={PRODUCT_KEY} title={strings.workspaces.none.title} pip="locked">
        <DoorText>{strings.workspaces.none.body}</DoorText>
        <DoorActions>
          <DoorButton asChild>
            <a href={signInUrl(centralUrl("/admin"))}>{strings.workspaces.none.signInAgain}</a>
          </DoorButton>
        </DoorActions>
        <DoorFine>
          {strings.workspaces.none.wrongAccount}{" "}
          <button type="button" className="door-link" onClick={() => signOut.mutate()}>
            {strings.auth.header.signOut}
          </button>
        </DoorFine>
      </Door>
    );
  }
  if (answer?.kind !== "signed_in") return <WorkspaceAccessNotice status={503} />;

  return (
    <Door product={PRODUCT_KEY} title={strings.workspaces.switcher.title} lead={strings.workspaces.switcher.lead}>
      <DoorChoices aria-label={strings.workspaces.switcher.list}>
        {answer.me.workspaces.map((workspace) => {
          const icon = organisationIcon(workspace.logos);
          return (
            <DoorChoice
              key={workspace.id}
              href={workspace.console_url}
              name={workspace.name}
              detail={strings.workspaces.roles[workspace.role]}
              // Its organisation's logo where Impa holds one, as the top bar shows it; the door's initial otherwise.
              mark={icon ? <OrganisationMark icon={icon} size={36} /> : undefined}
            />
          );
        })}
      </DoorChoices>
      {answer.me.unchecked > 0 && <DoorFine>{strings.workspaces.switcher.unchecked}</DoorFine>}
      <DoorFine>
        {strings.workspaces.switcher.notYou}{" "}
        <button type="button" className="door-link" onClick={() => signOut.mutate()}>
          {strings.auth.header.signOut}
        </button>
      </DoorFine>
    </Door>
  );
}

/** Off to sign in, which starts on the central host, and back to `returnTo` afterwards: Link's launch stays meanwhile. */
function SignIn({ returnTo }: { returnTo: string }) {
  useEffect(() => {
    window.location.replace(signInUrl(returnTo));
  }, [returnTo]);
  return <LaunchScreen product={PRODUCT_KEY} />;
}

export const Route = createFileRoute("/admin")({ component: Console });
